import React, { useState, useEffect } from "react";
import { Input, Button, Select, message, Form } from "antd";
import { ClipLoader } from "react-spinners";
import { useNavigate } from "react-router-dom";
import "./style.css";
import { getCustomerData } from "../../../API/User/customer";
import { addManualShutDown, getVehiclebyId } from "../../../API/User/shutdown";
import MapContainerComponent from "../../../Components/MapContainer";

const AddAutomatedShutdown = () => {
  const [form] = Form.useForm();
  const [editingRow, setEditingRow] = useState(null);
  const [editingValue, setEditingValue] = useState(null);
  const [activeRow, setActiveRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  const [isCustomerSelected, setIsCustomerSelected] = useState(false);
  const [mapLoading, setMapLoading] = useState(false);

  const [customers, setCustomers] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [vehicleOptions, setVehicleOptions] = useState([]);

  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [currentLat, setCurrentLat] = useState(null);
  const [currentLng, setCurrentLng] = useState(null);
  const [vehicleScore, setVehicleScore] = useState(null);
  const [scoreRows, setScoreRows] = useState([]);
  const [vehicleMap, setVehicleMap] = useState({});

  const navigate = useNavigate();
  const [data, setData] = useState([]);

  const errorNotification = (msg) => {
    messageApi.error(msg);
  };

  const successNotification = (msg) => {
    messageApi.success(msg);
  };

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomerData();
      const customersData = response?.data?.customers || [];

      setCustomers(customersData);

      const formattedCustomers = customersData.map((item) => ({
        label: item.customer_name,
        value: item.id,
      }));

      setCustomerOptions(formattedCustomers);
    } catch (error) {
      message.error("Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleCustomerChange = (customerId) => {
    const selectedCustomer = customers.find((c) => c.id === customerId);

    const vehicles = selectedCustomer?.vehicles || [];

    const map = {};

    vehicles.forEach((v) => {
      map[v.id] = v; // DB ID → full vehicle
    });

    setVehicleMap(map);

    setVehicleOptions(
      vehicles.map((v) => ({
        label: v.vehicle_name,
        value: v.id, // ONLY DB ID
      })),
    );

    setIsCustomerSelected(true);

    form.setFieldsValue({ vehicle: undefined });
  };

  const handleSubmit = async (values) => {
    try {
      const missingFields = [];

      if (!values.vehicle) missingFields.push("Vehicle");
      if (!values.reason) missingFields.push("Reason");
      if (values.riskScore == null) missingFields.push("Risk Score");

      if (missingFields.length > 0) {
        message.error(`Please fill: ${missingFields.join(", ")}`);
        return;
      }

      setLoading(true);

      const payload = {
        vehicle_id: values.vehicle,
        reason: values.reason,
        total_score: values.riskScore,
        latitude: currentLat,
        longitude: currentLng,
      };

      scoreRows.forEach((item) => {
        if (item.code) {
          payload[item.code] = Number(item.points || 0);
        }
      });

      await addManualShutDown(payload);

      form.resetFields();
      setScoreRows([]);

      messageApi.success("Shutdown added successfully!", 1, () => {
        navigate("/shut-down");
      });
    } catch (e) {
      console.error(e);
      message.error("Failed to add shutdown");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setSaved(false);
    message.info("Changes canceled");
  };

  const handleVehicleChange = async (dbId) => {
    const vehicle = vehicleMap[dbId];

    if (!vehicle) return;

    setSelectedVehicle(vehicle);

    try {
      setMapLoading(true);
      const sourceId = vehicle.source_id;

      const res = await getVehiclebyId(sourceId);
      const score = res?.data?.vehicle_score?.[0];

      if (!score) {
        message.error("No vehicle score found");
        setCurrentLat(null);
        setCurrentLng(null);
        return;
      }

      setCurrentLat(score.latitude);
      setCurrentLng(score.longitude);

      form.setFieldsValue({
        riskScore: score.total_score,
      });

      const formatted = Object.entries(score)
        .filter(
          ([key]) =>
            ![
              "source_id",
              "vehicle_name",
              "latitude",
              "longitude",
              "updated_at",
              "total_score",
            ].includes(key),
        )
        .map(([key, value]) => ({
          factor: key
            .split("_")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" "),
          points: value,
          code: key,
        }));

      setScoreRows(formatted);
    } catch (err) {
      console.error(err);
      message.error("Error fetching vehicle telemetry");
    } finally {
      setMapLoading(false);
    }
  };

  return (
    <div className="manual-shutdown">
      {contextHolder}
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <div className="shutdown-container">
          <div className="heading">Manual Shutdown</div>

          <div className="risk-details">
            <div className="input-group">
              <div className="title">Select Customer</div>
              <Form.Item
                name="customer"
                rules={[
                  { required: true, message: "Please select a customer!" },
                ]}
              >
                <Select
                  placeholder="Select Customer"
                  options={customerOptions}
                  onChange={handleCustomerChange}
                />
              </Form.Item>
            </div>

            <div className="input-group">
              <div className="title">Vehicle</div>
              <Form.Item
                name="vehicle"
                rules={[
                  { required: true, message: "Please select a vehicle!" },
                ]}
              >
                <Select
                  options={vehicleOptions}
                  placeholder="Select Vehicle"
                  disabled={!isCustomerSelected}
                  onChange={handleVehicleChange}
                />
              </Form.Item>
            </div>

            <div className="input-group">
              <div className="title">Risk Score</div>
              <Form.Item
                name="riskScore"
                rules={[
                  { required: true, message: "Please enter risk score!" },
                ]}
              >
                <Input disabled placeholder="Enter Risk score" />
              </Form.Item>
            </div>

            <div className="input-group">
              <div className="title">Reason</div>
              <Form.Item
                name="reason"
                rules={[{ required: true, message: "Please type reason!" }]}
              >
                <Input
                  placeholder="Enter Reason"
                  disabled={!isCustomerSelected}
                />
              </Form.Item>
            </div>
          </div>
        </div>
        <div
          className="shutdown-container-map"
          style={{ position: "relative", minHeight: "300px" }}
        >
          {mapLoading ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "300px",
              }}
            >
              <ClipLoader color="#1677ff" size={40} />
            </div>
          ) : currentLat && currentLng ? (
            <MapContainerComponent
              latitude={currentLat}
              longitude={currentLng}
              vehicle_name={selectedVehicle?.vehicle_name}
            />
          ) : (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
                minHeight: "300px",
                color: "#8c8c8c",
              }}
            >
              Please select a vehicle to display map location
            </div>
          )}
        </div>

        {/* TABLE */}
        <div className="factors-table">
          <div className="table-header">
            <span className="header-factor">Factors</span>
            <span className="header-points">Points</span>
          </div>

          {scoreRows.map((item, index) => (
            <div
              key={item.id || index}
              className={`table-row ${activeRow === index ? "selected" : ""}`}
            >
              <span className="factor">{item.factor}</span>

              <span className="points">
                {editingRow === index ? (
                  <Input
                    type="number"
                    value={editingValue}
                    onChange={(e) => setEditingValue(e.target.value)}
                    style={{ width: "60px" }}
                  />
                ) : (
                  item.points
                )}
              </span>
            </div>
          ))}
        </div>

        <div className="form-actions">
          <Button onClick={handleCancel}>Cancel</Button>
          <Button className="log-btn" type="primary" htmlType="submit">
            Add Log
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default AddAutomatedShutdown;
